import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import logger from '@/lib/logger';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart,
} from 'recharts';
import { ChartLineUp, Trophy, Clock, Barbell, Flame, Calendar, Target, Heart } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${API}/analytics/overview?days=90`);
        setData(resp.data);
      } catch (err) {
        logger.error('Analytics load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const heatmapWeeks = useMemo(() => {
    if (!data?.heatmap) return [];
    // Group heatmap entries into 13 weekly columns of 7 days each
    const days = data.heatmap;
    // Pad the beginning so each column starts on Monday
    const firstDay = days[0] ? new Date(days[0].date) : new Date();
    const padCount = (firstDay.getDay() + 6) % 7; // 0 = Mon
    const padded = [...Array(padCount).fill(null), ...days];
    const weeks = [];
    for (let i = 0; i < padded.length; i += 7) {
      weeks.push(padded.slice(i, i + 7));
    }
    return weeks;
  }, [data]);

  const heatColor = (count) => {
    if (!count) return 'bg-white/5';
    if (count === 1) return 'bg-[#FF3B30]/30';
    if (count === 2) return 'bg-[#FF3B30]/60';
    return 'bg-[#FF3B30]';
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="analytics-loading">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="tactical-card p-8 text-center" data-testid="analytics-empty">
        <p className="text-[#A1A1AA]">No analytics data available yet. Complete a few workouts to start tracking.</p>
      </div>
    );
  }

  const totals = data.totals || {};
  const pr = data.personal_records || {};
  const streak = data.streak || {};
  const wellness = data.wellness || {};

  return (
    <div data-testid="analytics-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Insights</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">ANALYTICS</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">Your last 90 days of training</p>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="tactical-card p-4" data-testid="kpi-workouts">
          <Barbell weight="bold" className="w-5 h-5 text-[#FF3B30] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{totals.workouts || 0}</div>
          <div className="label-tactical text-[#71717A] mt-1">Workouts</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-minutes">
          <Clock weight="bold" className="w-5 h-5 text-[#007AFF] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{totals.minutes || 0}</div>
          <div className="label-tactical text-[#71717A] mt-1">Minutes</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-exercises">
          <Target weight="bold" className="w-5 h-5 text-[#34C759] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{totals.exercises || 0}</div>
          <div className="label-tactical text-[#71717A] mt-1">Exercises</div>
        </div>
        <div className="tactical-card p-4" data-testid="kpi-streak">
          <Flame weight="bold" className="w-5 h-5 text-[#FF9500] mb-2" />
          <div className="text-3xl font-black font-['Barlow_Condensed']">{streak.current || 0}</div>
          <div className="label-tactical text-[#71717A] mt-1">Day Streak</div>
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="tactical-card p-5 mb-6" data-testid="weekly-trend-chart">
        <div className="flex items-center gap-2 mb-4">
          <ChartLineUp weight="bold" className="w-5 h-5 text-[#FF3B30]" />
          <h2 className="text-lg font-bold tracking-tight">12-Week Volume</h2>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.weekly_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
            <XAxis dataKey="week" stroke="#52525B" tick={{ fontSize: 10 }} tickFormatter={(w) => w.slice(5)} />
            <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', fontSize: 12 }}
              labelStyle={{ color: '#FF3B30' }}
            />
            <Bar dataKey="workouts" fill="#FF3B30" name="Workouts" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Day-of-week distribution */}
        <div className="tactical-card p-5" data-testid="day-of-week-chart">
          <div className="flex items-center gap-2 mb-4">
            <Calendar weight="bold" className="w-5 h-5 text-[#007AFF]" />
            <h2 className="text-lg font-bold tracking-tight">When You Train</h2>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.day_of_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
              <XAxis dataKey="day" stroke="#52525B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', fontSize: 12 }} />
              <Bar dataKey="count" fill="#007AFF" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Personal Records */}
        <div className="tactical-card p-5" data-testid="personal-records">
          <div className="flex items-center gap-2 mb-4">
            <Trophy weight="bold" className="w-5 h-5 text-[#FF9500]" />
            <h2 className="text-lg font-bold tracking-tight">Personal Records</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-white/5">
              <span className="text-sm text-[#A1A1AA]">Longest Workout</span>
              <span className="font-black font-['Barlow_Condensed'] text-2xl text-[#FF3B30]">{pr.longest_workout_minutes || 0}<span className="text-sm text-[#71717A] ml-1">min</span></span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-white/5">
              <span className="text-sm text-[#A1A1AA]">Most Exercises</span>
              <span className="font-black font-['Barlow_Condensed'] text-2xl text-[#34C759]">{pr.most_exercises_in_workout || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-white/5">
              <span className="text-sm text-[#A1A1AA]">Best Week</span>
              <span className="font-black font-['Barlow_Condensed'] text-2xl text-[#007AFF]">{pr.best_week_workouts || 0}<span className="text-sm text-[#71717A] ml-1">sessions</span></span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-[#A1A1AA]">Longest Streak</span>
              <span className="font-black font-['Barlow_Condensed'] text-2xl text-[#FF9500]">{streak.longest || 0}<span className="text-sm text-[#71717A] ml-1">days</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="tactical-card p-5 mb-6" data-testid="heatmap">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flame weight="bold" className="w-5 h-5 text-[#FF9500]" />
            <h2 className="text-lg font-bold tracking-tight">Activity Heatmap</h2>
          </div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#71717A]">
            Less
            <div className="w-3 h-3 bg-white/5" />
            <div className="w-3 h-3 bg-[#FF3B30]/30" />
            <div className="w-3 h-3 bg-[#FF3B30]/60" />
            <div className="w-3 h-3 bg-[#FF3B30]" />
            More
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex gap-1 min-w-fit">
            {heatmapWeeks.map((week, wi) => (
              <div key={`w-${wi}`} className="flex flex-col gap-1">
                {week.map((day, di) => (
                  <div
                    key={`d-${wi}-${di}`}
                    className={`w-3 h-3 ${day ? heatColor(day.count) : 'bg-transparent'}`}
                    title={day ? `${day.date}: ${day.count} workout${day.count === 1 ? '' : 's'}` : ''}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Wellness */}
      {wellness.logs_count > 0 && (
        <div className="tactical-card p-5 mb-6" data-testid="wellness-summary">
          <div className="flex items-center gap-2 mb-4">
            <Heart weight="fill" className="w-5 h-5 text-[#FF3B30]" />
            <h2 className="text-lg font-bold tracking-tight">Wellness Averages</h2>
            <span className="ml-auto text-xs text-[#71717A]">{wellness.logs_count} logs</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-tactical text-[#FF3B30] mb-1">Avg Mood</div>
              <div className="text-3xl font-black font-['Barlow_Condensed']">{wellness.avg_mood}/5</div>
            </div>
            <div>
              <div className="label-tactical text-[#007AFF] mb-1">Avg Energy</div>
              <div className="text-3xl font-black font-['Barlow_Condensed']">{wellness.avg_energy}/5</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
